import React, { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Upload,
  FileText,
  Image, // Image icon is still useful for general file representation if no specific TXT icon
  X,
  AlertCircle,
  Loader2,
  BookOpen,
  Table,
  Droplet,
  Shield,
  Lightbulb,
} from "lucide-react";
import { documentsAPI, machinesAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";

const typeOptions = [
  { value: "manual", label: "Manual", icon: BookOpen },
  { value: "sop", label: "SOP", icon: FileText },
  { value: "guide", label: "Guide", icon: Lightbulb },
  { value: "troubleshooting", label: "Troubleshooting", icon: AlertCircle },
  { value: "specification", label: "Specification", icon: Table },
  { value: "diagram", label: "Diagram", icon: Image },
  { value: "safety", label: "Safety", icon: Shield },
  { value: "other", label: "Other", icon: Droplet },
];

const DocumentUpload = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState({
    machineId: "",
    documentType: "manual",
    category: "",
    title: "",
    description: "",
  });

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  const uploadMutation = useMutation({
    mutationFn: (data) => documentsAPI.upload(data),
    onSuccess: (response) => {
      toast.success("Document uploaded successfully! AI processing started.");
      navigate("/knowledge");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Upload failed");
    },
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFiles = (newFiles) => {
    const validFiles = newFiles.filter((file) => {
      // MODIFICATION: Update validTypes to include only PDF and TXT
      const validTypes = ["application/pdf", "text/plain"];
      // Limit file size to 50MB
      const maxSize = 50 * 1024 * 1024;
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    if (validFiles.length !== newFiles.length) {
      // MODIFICATION: Update toast message for allowed file types
      toast.error(
        "Some files were rejected. Only PDF and TXT files (Max 50MB) are allowed.",
      );
    }

    // Only allow one file for now based on current backend implementation
    const fileToUpload = validFiles.length > 0 ? validFiles[0] : null;

    if (fileToUpload) {
      setFiles([
        {
          file: fileToUpload,
          id: Math.random().toString(36).substr(2, 9),
        },
      ]);
      setFormData((prev) => ({
        ...prev,
        title: fileToUpload.name.replace(/\.[^/.]+$/, ""), // Auto-fill title from filename
      }));
    } else {
      setFiles([]);
    }
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFiles(droppedFiles);
    },
    [handleFiles],
  );

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = null; // Reset input so same file can be selected again
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      toast.error("Please select a document to upload");
      return;
    }

    const form = new FormData();
    form.append("document", files[0].file);
    form.append("machineId", formData.machineId);
    form.append("documentType", formData.documentType);
    form.append("category", formData.category);
    form.append("title", formData.title || files[0].file.name);
    form.append("description", formData.description);

    uploadMutation.mutate(form);
  };

  const getFileIcon = (file) => {
    // MODIFICATION: Use FileText for PDF and TXT, as Image is no longer supported directly
    if (file.type === "application/pdf" || file.type === "text/plain")
      return FileText;
    return FileText; // Default to FileText for any other unsupported file type (though handleFiles should prevent this)
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const selectedType = typeOptions.find(
    (opt) => opt.value === formData.documentType,
  );

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Link
          to="/knowledge"
          className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Upload Document</h1>
          <p className="text-slate-500">
            Digitize manuals, SOPs, and documentation for the knowledge base.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Dropzone */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={clsx(
            "card p-6 border-2 border-dashed transition-all text-center rounded-xl",
            dragActive
              ? "border-blue-500 bg-blue-50 shadow-lg"
              : "border-slate-300 hover:border-slate-400 bg-white",
          )}
        >
          <input
            type="file"
            id="file-upload"
            className="hidden"
            // MODIFICATION: Update accept attribute for PDF and TXT
            accept=".pdf,.txt"
            // Note: Multiple is removed to enforce single file upload for now
            onChange={handleFileInput}
          />
          <label htmlFor="file-upload" className="cursor-pointer block">
            <Upload
              className={clsx(
                "w-14 h-14 mx-auto mb-4 transition-colors",
                dragActive ? "text-blue-600" : "text-slate-400",
              )}
            />
            <p className="text-xl font-semibold text-slate-800 mb-1">
              {dragActive ? "Drop the file now" : "Drag & drop a file here"}
            </p>
            <p className="text-sm text-slate-500 mb-4">
              or{" "}
              <span className="text-blue-600 font-medium">click to browse</span>
            </p>
            {/* MODIFICATION: Update descriptive text */}
            <p className="text-xs text-slate-400">
              Only PDF and TXT supported. Single file upload. Max 50MB.
            </p>
          </label>
        </motion.div>

        {/* Uploaded File Preview */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="card p-4 flex items-center gap-4 bg-slate-50 border border-slate-200"
          >
            {files.map((item) => {
              const FileIcon = getFileIcon(item.file);
              return (
                <div key={item.id} className="flex items-center gap-4 w-full">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <FileIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate">
                      {item.file.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatFileSize(item.file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(item.id)}
                    className="p-2 text-slate-500 hover:text-red-500 hover:bg-slate-200 rounded-full transition-colors"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* Document Details Form */}
        <div className="card p-6 space-y-5">
          <h3 className="font-bold text-lg text-slate-800 border-b pb-3 mb-4 border-slate-100">
            Document Metadata
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label font-medium text-slate-700">
                Document Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  required
                  value={formData.documentType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      documentType: e.target.value,
                    }))
                  }
                  className="input appearance-none pr-10"
                >
                  {typeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {selectedType && (
                  <selectedType.icon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                )}
              </div>
            </div>
            <div>
              <label className="label font-medium text-slate-700">
                Machine Association (Optional)
              </label>
              <select
                value={formData.machineId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    machineId: e.target.value,
                  }))
                }
                className="input"
              >
                <option value="">Select a machine...</option>
                {machines?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.model})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label font-medium text-slate-700">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g., ABC Machine Operating Manual v2.0"
              className="input"
            />
          </div>

          <div>
            <label className="label font-medium text-slate-700">
              Category (Optional Tag)
            </label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, category: e.target.value }))
              }
              placeholder="e.g., Hydraulic System, Safety"
              className="input"
            />
          </div>

          <div>
            <label className="label font-medium text-slate-700">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Brief description of the document content..."
              className="input min-h-[80px]"
            />
          </div>
        </div>

        {/* AI Processing Notice */}
        <div className="card p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">
                AI Knowledge Extraction
              </p>
              <p className="text-sm text-blue-700 mt-0.5">
                After upload, the document will enter a processing queue. Our AI
                will automatically extract key entities, Q&A pairs, and
                structure the content for quick search and conversational AI
                use. The status will update once complete.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
          <Link to="/knowledge" className="btn btn-ghost">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={files.length === 0 || uploadMutation.isPending}
            className="btn btn-primary"
          >
            {uploadMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Start Upload
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DocumentUpload;
